/**
 * Los cuatro paneles de la derecha: ficha, parentesco, revision y resumen.
 *
 * Cada uno es una funcion que recibe el contenedor y el contexto y lo pinta
 * entero. No hay estado propio ni diferencias incrementales: con cuatro
 * listas y un formulario, repintar es mas simple y mas fiable que llevar la
 * cuenta de lo que cambio, y el coste es irrelevante al lado del lienzo.
 */
import type { Indice } from "../core/grafo.js";
import type { Disposicion } from "../layout/layout.js";
import {
  apellidoPor,
  fechaTexto,
  nombreCompleto,
  nombrePrincipal,
  soloApellidos,
  type Arbol,
  type Persona,
} from "../core/model.js";
import { parentesco } from "../core/parentesco.js";
import { resumenIncidencias, type Incidencia } from "../core/validar.js";
import { botonPersona, campo, dato, el, selectDe, vaciar } from "./dom.js";
import { LEYENDA_SEXO } from "../render/tema.js";

export interface Contexto {
  arbol: Arbol;
  ix: Indice;
  disposicion: Disposicion;
  incidencias: Incidencia[];
  seleccion: string | undefined;
  parentescoA: string | undefined;
  parentescoB: string | undefined;
  /** Desde quien se leen los parentescos que salen bajo cada nombre. */
  perspectiva: string | undefined;
  /**
   * Si esta sesion solo puede mirar.
   *
   * Cuando es asi, los botones que cambian datos no se muestran. Dejarlos
   * puestos y fallar al guardar seria peor: la gente haria trabajo que se va
   * a perder.
   */
  soloLectura: boolean;
  nombreArchivo: string;

  seleccionar(handle: string): void;
  irA(handle: string): void;
  editarPersona(handle: string): void;
  editarRelaciones(handle: string): void;
  borrarPersona(handle: string): void;
  nuevaPersona(): void;
  ponerParentesco(a: string | undefined, b: string | undefined): void;
  ponerPerspectiva(handle: string | undefined): void;
}

const TIPOS_UNION: Record<string, string> = {
  Married: "casados",
  Unmarried: "pareja sin matrimonio",
  Unknown: "unión sin registrar",
};

/**
 * Etiqueta de hermandad, concordando con el sexo de la persona de la que se
 * habla. Para sexo sin registrar se da la forma doble, que es fea pero no
 * inventa un dato que no hay.
 */
function etiquetaHermandad(clase: string, sexo: string): string {
  const seg = (m: string, f: string): string =>
    sexo === "M" ? m : sexo === "F" ? f : `${m}/${f.slice(m.length - 1)}`;
  switch (clase) {
    case "completos":
      return seg("hermano", "hermana");
    case "medio-paterno":
      return `${seg("medio", "media")} ${seg("hermano", "hermana")} por el padre`;
    case "medio-materno":
      return `${seg("medio", "media")} ${seg("hermano", "hermana")} por la madre`;
    case "declarados":
      return "hermandad declarada, padres desconocidos";
    default:
      return clase;
  }
}

function iniciales(p: Persona): string {
  const n = nombrePrincipal(p);
  const a = n?.first?.trim()?.[0] ?? "";
  const b = n?.apellidos[0]?.texto.trim()?.[0] ?? "";
  return (a + b).toUpperCase() || "?";
}

function personasOrdenadas(arbol: Arbol): Array<{ handle: string; nombre: string }> {
  return [...arbol.personas.entries()]
    .map(([handle, p]) => ({ handle, nombre: `${nombreCompleto(p)} · ${p.id ?? ""}` }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
}

// ---------------------------------------------------------------------------
// Ficha
// ---------------------------------------------------------------------------

export function pintarFicha(caja: HTMLElement, ctx: Contexto): void {
  vaciar(caja);
  const h = ctx.seleccion;
  if (!h) {
    caja.appendChild(
      el("p", {
        clase: "vacio",
        texto:
          "Haz clic en una persona del diagrama para ver su ficha. " +
          "Pasa el cursor por encima para resaltar sus conexiones.",
      }),
    );
    return;
  }
  const p = ctx.arbol.personas.get(h);
  if (!p) return;

  caja.appendChild(
    el("div", {
      clase: "cabecera-ficha",
      hijos: [
        el("div", { clase: `insignia sexo-${p.sexo}`, texto: iniciales(p) }),
        el("div", {
          hijos: [
            el("h3", { texto: nombreCompleto(p) }),
            el("div", {
              clase: "sub",
              texto: `${p.id ?? h} · ${sexoTexto(p.sexo)}`,
            }),
          ],
        }),
      ],
    }),
  );

  // --- datos -------------------------------------------------------------
  const dl = el("dl", { clase: "datos" });
  const nombre = nombrePrincipal(p);

  if (nombre?.first) dl.append(...dato("Nombres", nombre.first));

  // El ORIGEN de cada apellido es el dato que se perderia al juntarlos en un
  // solo campo, asi que se muestra siempre.
  for (const a of nombre?.apellidos ?? []) {
    const valor = el("span", { texto: a.texto });
    valor.appendChild(
      el("span", {
        clase: "etiqueta-origen",
        texto:
          a.derivation === "Patrilineal"
            ? "(del padre)"
            : a.derivation === "Matrilineal"
              ? "(de la madre)"
              : a.derivation
                ? `(${a.derivation})`
                : "(origen sin registrar)",
      }),
    );
    dl.append(...dato(a.prim ? "Primer apellido" : "Segundo apellido", valor));
  }
  if (!nombre?.apellidos.length) {
    dl.append(...dato("Apellidos", "sin registrar"));
  }

  const nac = eventoDe(ctx.arbol, p, "Birth");
  const mue = eventoDe(ctx.arbol, p, "Death");
  dl.append(...dato("Nacimiento", textoEvento(ctx.arbol, nac) ?? "sin registrar"));
  if (mue) dl.append(...dato("Fallecimiento", textoEvento(ctx.arbol, mue) ?? "sin fecha"));

  const fila = ctx.disposicion.generaciones.fila.get(h);
  const linaje = ctx.disposicion.generaciones.linaje.get(h);
  dl.append(...dato("Fila del diagrama", String(fila ?? "—")));
  dl.append(...dato("Generación de linaje", String(linaje ?? "—")));
  caja.appendChild(dl);

  // --- relaciones --------------------------------------------------------
  const progenitores = ctx.ix.progenitores(h);
  const bloque = (titulo: string, items: HTMLElement[]): void => {
    caja.appendChild(el("h4", { clase: "seccion", texto: titulo }));
    if (!items.length) {
      caja.appendChild(el("p", { clase: "vacio", texto: "—" }));
      return;
    }
    caja.appendChild(el("ul", { clase: "lista", hijos: items }));
  };

  bloque(
    "Progenitores",
    progenitores.flatMap((g) => {
      const out: HTMLElement[] = [];
      for (const [rol, quien] of [
        ["padre", g.padre],
        ["madre", g.madre],
      ] as const) {
        if (!quien) continue;
        const q = ctx.arbol.personas.get(quien);
        if (!q) continue;
        out.push(botonPersona(nombreCompleto(q), rol, () => ctx.seleccionar(quien)));
      }
      if (!g.padre && !g.madre) {
        out.push(
          el("li", {
            hijos: [
              el("p", {
                clase: "vacio",
                texto: "familia sin padre ni madre registrados",
              }),
            ],
          }),
        );
      }
      return out;
    }),
  );

  bloque(
    "Hermanos",
    ctx.ix.hermanos(h).map((herm) => {
      const q = ctx.arbol.personas.get(herm.otra);
      return botonPersona(
        q ? nombreCompleto(q) : herm.otra,
        etiquetaHermandad(herm.clase, q?.sexo ?? "U"),
        () => ctx.seleccionar(herm.otra),
      );
    }),
  );

  bloque(
    "Parejas",
    ctx.ix.parejas(h).map((v) => {
      const q = ctx.arbol.personas.get(v.otra);
      return botonPersona(
        q ? nombreCompleto(q) : v.otra,
        TIPOS_UNION[v.rel] ?? v.rel,
        () => ctx.seleccionar(v.otra),
      );
    }),
  );

  bloque(
    "Hijos",
    ctx.ix.hijosDe(h).map((c) => {
      const q = ctx.arbol.personas.get(c);
      return botonPersona(q ? nombreCompleto(q) : c, undefined, () => ctx.seleccionar(c));
    }),
  );

  // --- acciones ----------------------------------------------------------
  const acciones = el("div", { clase: "acciones" });
  const boton = (texto: string, clase: string, accion: () => void): void => {
    const b = el("button", { texto, clase });
    b.addEventListener("click", accion);
    acciones.appendChild(b);
  };
  if (ctx.perspectiva === h) {
    boton("Quitar el punto de vista", "", () => ctx.ponerPerspectiva(undefined));
  } else {
    boton("Ver el árbol desde aquí", "primario", () => ctx.ponerPerspectiva(h));
  }
  boton("Centrar", "", () => ctx.irA(h));
  boton("Medir parentesco", "", () => ctx.ponerParentesco(h, ctx.parentescoB));
  if (!ctx.soloLectura) {
    boton("Editar datos", "", () => ctx.editarPersona(h));
    boton("Relaciones", "", () => ctx.editarRelaciones(h));
    boton("Borrar", "peligro", () => ctx.borrarPersona(h));
  }
  caja.appendChild(acciones);

  if (ctx.soloLectura) {
    caja.appendChild(
      el("p", {
        clase: "nota-pie",
        texto:
          "Tu acceso es de solo lectura: puedes mirar el árbol y medir " +
          "parentescos, pero no cambiarlo.",
      }),
    );
  }

  if (ctx.perspectiva === h) {
    caja.appendChild(
      el("p", {
        clase: "es-perspectiva",
        texto:
          "El árbol se está leyendo desde esta persona: debajo de cada nombre " +
          "aparece el parentesco con ella. Quien no tenga parentesco no lleva " +
          "etiqueta.",
      }),
    );
  }
}

function sexoTexto(s: string): string {
  if (s === "M") return "hombre";
  if (s === "F") return "mujer";
  return "sexo sin registrar";
}

function eventoDe(
  arbol: Arbol,
  p: Persona,
  tipo: string,
): { fecha?: Parameters<typeof fechaTexto>[0]; lugar?: string } | undefined {
  for (const r of p.eventos) {
    const e = arbol.eventos.get(r.hlink);
    if (e?.tipo === tipo) return { fecha: e.fecha, lugar: e.lugar };
  }
  return undefined;
}

function textoEvento(
  arbol: Arbol,
  ev: { fecha?: Parameters<typeof fechaTexto>[0]; lugar?: string } | undefined,
): string | undefined {
  if (!ev) return undefined;
  const f = fechaTexto(ev.fecha);
  const l = ev.lugar ? arbol.lugares.get(ev.lugar)?.nombres[0]?.value : undefined;
  const partes = [f, l].filter(Boolean);
  return partes.length ? partes.join(" · ") : undefined;
}

// ---------------------------------------------------------------------------
// Parentesco
// ---------------------------------------------------------------------------

export function pintarParentesco(caja: HTMLElement, ctx: Contexto): void {
  vaciar(caja);
  const lista = personasOrdenadas(ctx.arbol);
  const opciones = [{ valor: "", texto: "— elegir —" }, ...lista.map((x) => ({ valor: x.handle, texto: x.nombre }))];

  const selA = selectDe(opciones, ctx.parentescoA ?? "", (v) =>
    ctx.ponerParentesco(v || undefined, ctx.parentescoB),
  );
  const selB = selectDe(opciones, ctx.parentescoB ?? "", (v) =>
    ctx.ponerParentesco(ctx.parentescoA, v || undefined),
  );

  const selectores = el("div", {
    clase: "selectores",
    hijos: [
      campo("Desde", selA, "La respuesta se lee desde esta persona."),
      campo("Hasta", selB),
    ],
  });
  caja.appendChild(selectores);

  const intercambiar = el("button", { texto: "Invertir" });
  intercambiar.addEventListener("click", () =>
    ctx.ponerParentesco(ctx.parentescoB, ctx.parentescoA),
  );
  const usarSeleccion = el("button", { texto: "Usar la persona seleccionada" });
  usarSeleccion.disabled = !ctx.seleccion;
  usarSeleccion.addEventListener("click", () =>
    ctx.ponerParentesco(ctx.seleccion, ctx.parentescoB),
  );
  caja.appendChild(el("div", { clase: "acciones", hijos: [intercambiar, usarSeleccion] }));

  if (!ctx.parentescoA || !ctx.parentescoB) {
    caja.appendChild(
      el("p", {
        clase: "vacio",
        texto:
          "Elige dos personas para ver el parentesco exacto. " +
          "Funciona con medios hermanos, uniones múltiples y parentescos por afinidad.",
      }),
    );
    return;
  }

  const r = parentesco(ctx.ix, ctx.parentescoA, ctx.parentescoB);
  const a = ctx.arbol.personas.get(ctx.parentescoA);
  const b = ctx.arbol.personas.get(ctx.parentescoB);

  const resultado = el("div", { clase: "resultado" });
  resultado.appendChild(el("div", { clase: "termino", texto: r.termino }));
  resultado.appendChild(
    el("div", {
      clase: "frase",
      texto: `${b ? nombreCompleto(b) : "?"} ${r.explicacion}` +
        (a ? ` (leído desde ${nombreCompleto(a)})` : ""),
    }),
  );

  for (const n of r.notas ?? []) {
    resultado.appendChild(el("div", { clase: "nota", texto: n }));
  }

  const tecnico: string[] = [`vía: ${r.clase}`];
  if (r.subidaA !== undefined) tecnico.push(`subidas ${r.subidaA}/${r.subidaB}`);
  if (r.via) tecnico.push(`rama ${r.via}`);
  if (r.mediaSangre) tecnico.push("media sangre");
  if (r.gradoPrimo) tecnico.push(`grado de primazgo ${r.gradoPrimo}`);
  if (r.remocion) tecnico.push(`removido ${r.remocion}`);
  if (r.ancestros?.length) {
    const nombres = r.ancestros
      .map((x) => ctx.arbol.personas.get(x))
      .filter((x): x is Persona => !!x)
      .map((x) => nombreCompleto(x));
    tecnico.push(`ascendiente común: ${nombres.join(" y ")}`);
  }
  resultado.appendChild(el("div", { clase: "tecnico", texto: tecnico.join("  ·  ") }));
  caja.appendChild(resultado);

  const ir = el("div", { clase: "acciones" });
  for (const [etiqueta, handle] of [
    ["Centrar en la primera", ctx.parentescoA],
    ["Centrar en la segunda", ctx.parentescoB],
  ] as const) {
    const bt = el("button", { texto: etiqueta });
    bt.addEventListener("click", () => ctx.irA(handle));
    ir.appendChild(bt);
  }
  caja.appendChild(ir);
}

// ---------------------------------------------------------------------------
// Revision
// ---------------------------------------------------------------------------

export function pintarRevision(caja: HTMLElement, ctx: Contexto): void {
  vaciar(caja);
  const res = resumenIncidencias(ctx.incidencias);

  const conteo = el("div", { clase: "conteo" });
  if (!ctx.incidencias.length) {
    conteo.appendChild(el("span", { clase: "pastilla ok", texto: "sin incidencias" }));
  } else {
    if (res.error) {
      conteo.appendChild(
        el("span", { clase: "pastilla error", texto: `${res.error} error(es)` }),
      );
    }
    if (res.aviso) {
      conteo.appendChild(
        el("span", { clase: "pastilla aviso", texto: `${res.aviso} aviso(s)` }),
      );
    }
    if (res.info) {
      conteo.appendChild(el("span", { clase: "pastilla info", texto: `${res.info} nota(s)` }));
    }
  }
  caja.appendChild(conteo);

  caja.appendChild(
    el("p", {
      clase: "vacio",
      texto:
        "Los avisos no bloquean nada: a veces la realidad es irregular y a " +
        "veces es un error de dedo. Tú decides.",
    }),
  );

  const orden = { error: 0, aviso: 1, info: 2 } as const;
  for (const i of [...ctx.incidencias].sort(
    (a, b) => orden[a.severidad] - orden[b.severidad],
  )) {
    const caja2 = el("div", { clase: `incidencia ${i.severidad}` });
    caja2.appendChild(el("span", { clase: "codigo", texto: i.codigo }));
    caja2.appendChild(el("div", { texto: i.mensaje }));
    const primera = i.personas[0];
    if (primera && ctx.arbol.personas.has(primera)) {
      const b = el("button", { clase: "ir", texto: "ver en el diagrama →" });
      b.addEventListener("click", () => {
        ctx.seleccionar(primera);
        ctx.irA(primera);
      });
      caja2.appendChild(b);
    }
    caja.appendChild(caja2);
  }
}

// ---------------------------------------------------------------------------
// Resumen
// ---------------------------------------------------------------------------

export function pintarResumen(caja: HTMLElement, ctx: Contexto): void {
  vaciar(caja);
  const { arbol, ix, disposicion } = ctx;

  const filas: Array<[string, string]> = [];
  filas.push(["Archivo", ctx.nombreArchivo]);
  filas.push(["Formato", `Gramps XML ${arbol.version}`]);
  filas.push(["Personas", String(arbol.personas.size)]);
  filas.push(["Familias", String(arbol.familias.size)]);
  filas.push(["Eventos", String(arbol.eventos.size)]);
  filas.push(["Lugares", String(arbol.lugares.size)]);

  const deriv = { Patrilineal: 0, Matrilineal: 0, otros: 0 };
  let sinSegundo = 0;
  let sinNombrePila = 0;
  for (const p of arbol.personas.values()) {
    const n = nombrePrincipal(p);
    if ((n?.apellidos.length ?? 0) < 2) sinSegundo++;
    if (!n?.first?.trim()) sinNombrePila++;
    for (const a of n?.apellidos ?? []) {
      if (a.derivation === "Patrilineal") deriv.Patrilineal++;
      else if (a.derivation === "Matrilineal") deriv.Matrilineal++;
      else deriv.otros++;
    }
  }
  filas.push(["Apellidos del padre", String(deriv.Patrilineal)]);
  filas.push(["Apellidos de la madre", String(deriv.Matrilineal)]);
  if (deriv.otros) filas.push(["Apellidos de otro origen", String(deriv.otros)]);
  filas.push(["Con un solo apellido", String(sinSegundo)]);
  filas.push(["Sin nombre de pila", String(sinNombrePila)]);

  const rels = new Map<string, number>();
  for (const f of arbol.familias.values()) {
    const r = f.rel ?? "sin registrar";
    rels.set(r, (rels.get(r) ?? 0) + 1);
  }
  for (const [r, n] of rels) filas.push([`Uniones: ${TIPOS_UNION[r] ?? r}`, String(n)]);

  const soloMadre = [...arbol.familias.values()].filter((f) => f.madre && !f.padre).length;
  const soloPadre = [...arbol.familias.values()].filter((f) => f.padre && !f.madre).length;
  const sinNadie = [...arbol.familias.values()].filter((f) => !f.padre && !f.madre).length;
  const sinHijos = [...arbol.familias.values()].filter(
    (f) => f.padre && f.madre && !f.hijos.length,
  ).length;
  filas.push(["Familias solo con madre", String(soloMadre)]);
  filas.push(["Familias solo con padre", String(soloPadre)]);
  filas.push(["Familias sin padre ni madre", String(sinNadie)]);
  filas.push(["Parejas sin hijos", String(sinHijos)]);

  let medios = 0;
  for (const h of ix.todas()) {
    for (const x of ix.hermanos(h)) {
      if (x.clase === "medio-paterno" || x.clase === "medio-materno") medios++;
    }
  }
  filas.push(["Pares de medios hermanos", String(medios / 2)]);
  filas.push([
    "Progenitores con varias uniones",
    String(ix.todas().filter((h) => ix.familiasComoProgenitor(h).length > 1).length),
  ]);
  filas.push(["Sin fecha ni lugar", String(ix.todas().filter((h) => !arbol.personas.get(h)?.eventos.length).length)]);

  const tabla = el("table", { clase: "resumen" });
  for (const [k, v] of filas) {
    tabla.appendChild(
      el("tr", {
        hijos: [el("th", { texto: k }), el("td", { clase: "num", texto: v })],
      }),
    );
  }
  caja.appendChild(tabla);

  caja.appendChild(el("h4", { clase: "seccion", texto: "Disposición" }));
  const t2 = el("table", { clase: "resumen" });
  const reparto = new Map<number, number>();
  for (const n of disposicion.fisica.nodos.values()) {
    reparto.set(n.fila, (reparto.get(n.fila) ?? 0) + 1);
  }
  for (const [f, n] of [...reparto].sort((a, b) => a[0] - b[0])) {
    t2.appendChild(
      el("tr", {
        hijos: [
          el("th", { texto: `Fila ${f}${f === 0 ? " (la más antigua)" : ""}` }),
          el("td", { clase: "num", texto: `${n} personas` }),
        ],
      }),
    );
  }
  for (const [k, v] of [
    ["Cruces de líneas", String(disposicion.cruces)],
    ["Pasos hasta asentarse", String(disposicion.pasosAsentado)],
  ] as const) {
    t2.appendChild(
      el("tr", { hijos: [el("th", { texto: k }), el("td", { clase: "num", texto: v })] }),
    );
  }
  caja.appendChild(t2);

  caja.appendChild(
    el("p", {
      clase: "nota-pie",
      texto:
        "La fila del diagrama agrupa parejas y hermanos; la generación de " +
        "linaje cuenta solo descendencia. Los dos números salen en la ficha " +
        "de cada persona y el criterio está en docs/GENERACIONES.md.",
    }),
  );
}

// ---------------------------------------------------------------------------
// Leyenda
// ---------------------------------------------------------------------------

/** La leyenda repite forma Y color, igual que el diagrama. */
export function pintarLeyenda(caja: HTMLElement): void {
  vaciar(caja);
  for (const e of LEYENDA_SEXO) {
    if (!e) continue;
    caja.appendChild(
      el("span", {
        clase: "it",
        hijos: [
          el("span", { clase: `marca-forma forma-${e.forma}` }),
          el("span", { texto: e.etiqueta }),
        ],
      }),
    );
  }
}

/** Nombre corto para las listas de busqueda. */
export function etiquetaBusqueda(p: Persona): string {
  const aps = soloApellidos(p);
  const pat = apellidoPor(p, "Patrilineal");
  const mat = apellidoPor(p, "Matrilineal");
  const detalle = [pat && `padre: ${pat}`, mat && `madre: ${mat}`]
    .filter(Boolean)
    .join(" · ");
  return `${nombreCompleto(p)}${aps && detalle ? `  —  ${detalle}` : ""}`;
}
